import moment from "moment";
import { mapGetters, mapState, mapActions } from "vuex";
import {
  getPairsInfo as getPairsTrade,
  getCurrencies as getCurrenciesTrade,
  getBalance as getTradeBalance
} from "@/api";
import {
  getPairsInfo as getPairsKyc,
  getCurrencies as getCurrenciesKyc,
  queryPaymentDetailsByIntentId
} from "@/api/kyc";
import { checkProcess as kycV2CheckProcess } from "@/api/v2/kyc";

import { getCurrencyUnitList, displayErrors } from "@/utils";

export const authMixin = {
  computed: {
    ...mapGetters([
      "isAccountDisabled",
      "isAccountEnabled",
      "asset",
      "baseUrlTrade",
      "allowedLangs",
      "isExternalCheckoutProcess",
      "isDexWalletUserFlow"
    ]),
    ...mapState([
      "themeColor",
      "clientInfo",
      "token",
      "expiresIn",
      "kycStatus",
      "nextKycStep",
      "exchangeConfig",
      "bgThemeColor",
      "baseModalVisible",
      "exchangeName",
      "accountEmail",
      "user_preference",
      "kycProcessSteps",
      "walletToken"
    ])
  },
  methods: {
    ...mapActions([
      "toggleMaintenance",
      "updateKycStatus",
      "updateConnectStatus",
      "updateAccountEmail",
      "updateAccountType",
      "updateExpiresIn",
      "updateExpiresAt",
      "updateExchangeConfig",
      "updateClientInfo",
      "setThemeColor",
      "setToken",
      "setPairs",
      "updateUserPreference",
      "setCurrencies",
      "updateAsset",
      "updateTradePair",
      "setBgThemeColor",
      "updateNextKycStep",
      "updateBaseModalVisible",
      "setBalance",
      "updateExchangeName",
      "setRefreshToken",
      "updateTokenExpiresDate",
      "setupRfqParams",
      "updateTokenType",
      "updateIsHighRiskIp",
      "updateIpAddressInfo",
      "updateOriginalNextKycStep",
      "updateCurrencyUnitList",
      "updateForbiddenInfo",
      "updateUserInfo",
      "updateModalView",
      "updateAccountStatus",
      "setCheckKycProcessList",
      "updateTradeQuantity",
      "updateExchangeLogoUrl"
    ]),
    handlePairsData(pairs, defaultAsset) {
      // 先找设定的法币对应的交易对，找到就正常展示
      const set = new Set();
      const defaultSet = new Set();
      pairs.forEach(itemPair => {
        Object.keys(itemPair).forEach(pair => {
          let v = itemPair[pair];
          const vArr = v?.display?.split(" / ") ?? "";
          if (defaultAsset === vArr[1]) {
            vArr.length && set.add(vArr[0]);
          } else {
            vArr.length && defaultSet.add(vArr[0]);
          }
        });
      });
      const arr = Array.from(!set.size ? defaultSet : set);
      return arr;
    },
    async getSign(data) {
      const {
        access_token,
        expires_in,
        kyc_status,
        connect_status,
        account_email,
        account_type,
        exchange_config,
        user_preference,
        next_kyc_step = "questionnaire",
        refresh_token = "",
        token_type = "",
        expires_at = "",
        user_info = {
          edd: {},
          rfi: {}
        },
        account_status
      } = data;

      if (kyc_status === "Refresh Needed") {
        this.updateModalView("RefreshNeededPending");
        this.updateBaseModalVisible(true);
      }

      if (account_status === "inactive") {
        this.updateAccountStatus(account_status);
        this.$router.push({
          name: "refuses",
          params: { error: "inactive_user" }
        });
        return;
      }

      const isRefusesed =
        exchange_config?.allowed_payment_methods?.length === 0 ?? false;
      if (isRefusesed) {
        const remoteConfigIpDisabled = user_preference?.ip_disabled ?? false;
        this.updateIsHighRiskIp(remoteConfigIpDisabled);

        const remoteConfigIpAddressInfo = user_preference?.ip_address_info ?? {
          country: "",
          state: ""
        };
        this.updateIpAddressInfo(remoteConfigIpAddressInfo);

        this.$router.push({ name: "refuses" });
        return;
      }

      this.updateNextKycStep(next_kyc_step);
      this.updateKycStatus(kyc_status);
      this.updateConnectStatus(connect_status);

      if (next_kyc_step === "redo_verify_id") {
        this.updateOriginalNextKycStep(next_kyc_step); // 记录原始的nextKycStep数据,针对WEB-375
      }

      this.updateUserPreference(user_preference ?? { default_fiat: null });

      const remoteConfigNoSell = exchange_config?.no_sell;
      const localConfigNoSell = this.clientInfo.noSell;
      this.updateClientInfo({
        noSell: remoteConfigNoSell ?? localConfigNoSell
      });

      if (account_email) {
        this.updateAccountEmail(account_email);
      }
      this.updateAccountType(account_type?.toLowerCase() ?? "individual");
      this.setToken(access_token);
      this.setRefreshToken(refresh_token);

      this.updateExpiresIn(expires_in);
      this.updateExpiresAt(expires_at);
      this.updateTokenType(token_type);
      if (expires_in) {
        this.updateTokenExpiresDate(
          moment()
            .add(expires_in ?? 0, "seconds")
            .format()
        );
      }

      if (token_type === "Bearer") {
        // get sardine session after authorize
        // emitter.emit("legend-sardine:load", "authorize");
        // load checkout risk script
        // emitter.emit("legend-checkout:load-risk");
      }

      this.updateExchangeLogoUrl(exchange_config?.logo_url ?? "");
      this.updateExchangeName(exchange_config?.exchange_name ?? "");
      this.updateExchangeConfig(exchange_config);
      this.updateUserInfo(user_info);

      await this.getAuth();

      await this.navigateToByStatus(kyc_status, connect_status);

      return;
    },
    async navigateToByStatus(kycStatus, connectStatus) {
      const { name, path } = this.$route;
      const { customRouteEntry } = this;
      if (name === "refuses") return;
      if (connectStatus === "Pending") {
        // redirect to pending entry for kyc connect
        const pending = "register.pending";
        name !== pending && this.$router.push({ name: pending });
      } else if (connectStatus === "QA") {
        const kycSurvey = "individual.kycSurvey";
        name !== kycSurvey && this.$router.push({ name: kycSurvey });
      } else {
        if (customRouteEntry) {
          if (kycStatus === "Approved" && this.blackList(customRouteEntry)) {
            path !== customRouteEntry && this.$router.push(customRouteEntry);
          } else if (
            ["Not Started", "Started", "In Progress"].includes(kycStatus) &&
            customRouteEntry === "/kyc"
          ) {
            path !== customRouteEntry && this.$router.push(customRouteEntry);
          } else {
            await this.navigateByStatus(kycStatus, name);
          }
        } else {
          await this.navigateByStatus(kycStatus, name);
        }
      }
    },
    blackList(path) {
      return (
        ![
          "/register",
          "/register/",
          "register.individual",
          "/register/accountType",
          "/kyc",
          "/plaid",
          "/applyDone",
          "/maintain"
        ].includes(path) &&
        !path?.startsWith("/individual") &&
        !path?.startsWith("/additional")
      );
    },
    async getPaymentDetailsByIntentId() {
      const result = await queryPaymentDetailsByIntentId({
        payment_intent_id: this.clientInfo.paymentIntentId
      });
      const data = result.data[0];

      this.updateAsset(data.fiat);
      this.updateTradePair(data.crypto);
      this.updateTradeQuantity(data.size);
    },
    async navigateByStatus(kycStatus, name) {
      switch (kycStatus) {
        case "Not Started":
        case "Started":
        case "In Progress": {
          // !name?.startsWith("register") && router.push({ name: "register" });

          await this.getCheckProcessList();
          if (this.isExternalCheckoutProcess) {
            const pathName = this.operateKycProcessSteps();
            this.$router.push({ name: pathName });
          } else if (this.isDexWalletUserFlow) {
            await this.getPaymentDetailsByIntentId();
            const pathName = this.operateKycProcessSteps();
            this.$router.push({ name: pathName });
          } else {
            !name?.startsWith("register") &&
              this.$router.push({ name: "register" });
          }
          break;
        }
        case "Submitted":
        case "In Review":
        case "Rejected": {
          if (this.isExternalCheckoutProcess) {
            !name?.startsWith("individual.verifyFailed") &&
              this.$router.push({ name: "individual.verifyFailed" });
          } else if (this.isDexWalletUserFlow) {
            !name?.startsWith("individual.verifyFailed") &&
              this.$router.push({ name: "individual.verifyFailed" });
          } else {
            !name?.startsWith("home") &&
              this.$router.push({ name: "home.trade" });
          }
          break;
        }
        case "Approved": {
          if (this.isExternalCheckoutProcess) {
            !name?.startsWith("external.checkout") &&
              this.$router.push({ name: "external.checkout" });
          } else if (this.isDexWalletUserFlow) {
            !name?.startsWith("dex.buy.confirm") &&
              this.$router.push({ name: "dex.buy.confirm" });
          } else {
            !name?.startsWith("home") &&
              this.$router.push({ name: "home.trade" });
          }
          break;
        }
        default: {
          break;
        }
      }
    },
    async getCheckProcessList() {
      try {
        const result = await kycV2CheckProcess();
        if (result.status === "success") {
          const { fields } = result.data[0];
          const checkProcessList = fields;
          this.setCheckKycProcessList(checkProcessList);
        } else {
          displayErrors(result);
        }
      } catch (err) {
        displayErrors(err);
      }
    },
    async getAuth() {
      try {
        // TOOD: error handler of Promise.all
        const [pairs, currencies, balance] = await Promise.all([
          this.getPairs(),
          this.getCurrency(),
          this.getBalance()
        ]);

        const currencyUnitList = getCurrencyUnitList(currencies);

        this.setPairs(pairs);
        this.setCurrencies(currencies);
        this.updateCurrencyUnitList(currencyUnitList);
        this.setBalance(balance);

        const currencyInfo = currencies.find(
          item => item.currency === this.user_preference?.default_fiat
        );

        let defaultAsset = "";

        if (!currencyInfo) {
          // 没有找到，使用currencies的第一个数据做为默认的法币
          defaultAsset = currencies[0].currency;
          this.updateAsset(defaultAsset);
        } else {
          // 找到了就用当前的数据做为默认法币
          defaultAsset = currencyInfo.currency;
          this.updateAsset(defaultAsset);
        }

        const pairsList = this.handlePairsData(pairs, defaultAsset);
        const currentPair = pairsList[0];
        this.updateTradePair(currentPair);
      } catch (err) {
        if (err.response) {
          const { code, maintenance_mode } = err.response.data;
          if (code === "10401") {
            // account email occupied
            this.showGuide = false;
            this.$router.push({ name: "refuses", params: { error: "email" } });
          } else if (maintenance_mode) {
            this.toggleMaintenance(true);
            this.showGuide = false;
            this.$router.push("/maintain");
          }
        }
        displayErrors(err);

        this.setBalance({
          crypto_exchange: {},
          crypto: {},
          fiat: {}
        });
      } finally {
        this.setupRfqParams();
      }
    },
    getPairs() {
      return this.isAccountEnabled ? getPairsTrade() : getPairsKyc();
    },
    getCurrency() {
      return this.isAccountEnabled ? getCurrenciesTrade() : getCurrenciesKyc();
    },
    getBalance() {
      return this.isAccountEnabled && getTradeBalance();
    }
  }
};
